import React, { type ReactNode } from "react"

import { cn } from "../../utils/cn.ts"

export interface ArchiveTabOption {
  value: string
  label: string
  icon: ReactNode
  /** Omitted while the count isn't known yet, rather than showing a false 0. */
  count?: number
}

interface ArchiveTabsProps {
  options: ArchiveTabOption[]
  value: string
  onChange: (value: string) => void
}

/**
 * A pill-style segmented control for the Archive header. Visually heavier
 * than antd's `Segmented` (icon + label + count per option) but the same
 * single-select tab semantics, so it reads as EZBuild's own chip language
 * rather than a new pattern.
 */
export function ArchiveTabs({ options, value, onChange }: ArchiveTabsProps) {
  return (
    <div
      role={"tablist"}
      className={"inline-flex items-center gap-1 rounded-full bg-muted p-1"}
    >
      {options.map((option) => {
        const active = option.value === value

        return (
          <button
            key={option.value}
            type={"button"}
            role={"tab"}
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-medium transition-colors",
              active
                ? "bg-white text-primary shadow-card"
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            <span
              className={cn(
                "text-base",
                active ? "text-primary" : "text-gray-400"
              )}
            >
              {option.icon}
            </span>
            {option.label}
            {option.count !== undefined && (
              <span
                className={cn(
                  "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold",
                  active
                    ? "bg-primary/10 text-primary"
                    : "bg-gray-200 text-gray-600"
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
